/**
 * RISKS-FAMILIES.ROUTE
 * Catalogue des risques d'un etablissement, par famille.
 * Onglet "Risques physiques" (/risks/phy) ; les RPS ont leur matrice (risks-rps.route).
 */
import { useAppDispatch, useAppSelector } from "@/core/root-reducer"
import physicalRiskStore from "@/entities/physical-risk/model/physical-risk.store"
import RiskFamiliesFilterPanel from "@/entities/risk-family/ui/RiskFamiliesFilterPanel"
import RiskFamilies from "@/entities/risk-family/ui/RiskFamilies"
import siteStore from "@/entities/site/model/site.store"
import populationStore from "@/entities/population/model/population.store"
import riskFamilyStore from "@/entities/risk-family/model/risk-family.store"
import PhysicalRiskCard from "@/entities/physical-risk/ui/PhysicalRiskCard"
import PhysicalRiskEditModal from "@/entities/physical-risk/ui/modal/PhysicalRiskEditModal"
import { faPlus } from "@fortawesome/free-solid-svg-icons"
import { useEffect, useRef, useState } from "react"
import PageHeader from "@/components/page-header"
import { Navigate, useNavigate } from "react-router-dom"
import NavEstablishment from "@/nav/nav.establishment"
import { useRisksSpace } from "@/core/risks-space.context"
import { filterRiskFamiliesWithRisks, RISK_FAMILIES_PATH } from "@/entities/risk-family/model/risk-family.utils"

export default function RisksFamiliesRoute() {
  const dispatch = useAppDispatch()
  const navigate = useNavigate()
  const { getPath } = useRisksSpace()

  //Selectors
  const search = useAppSelector(physicalRiskStore.selectors.search)
  const selectedSites = useAppSelector(siteStore.selectors.selected)
  const selectedPopulations = useAppSelector(populationStore.selectors.selected)
  const risks = useAppSelector(physicalRiskStore.selectors.listFiltered)
  const activeRisk = useAppSelector(physicalRiskStore.selectors.active)
  const hasActiveFilters = Boolean(search.value || selectedSites.length || selectedPopulations.length)
  const hadActiveFilters = useRef(hasActiveFilters)

  const families = useAppSelector(physicalRiskStore.selectors.listFamilies)

  //State
  const [isAddModalOpen, setIsAddModalOpen] = useState(false)

  useEffect(() => {
    if (hadActiveFilters.current && !hasActiveFilters) {
      dispatch(riskFamilyStore.actions.setActive(null))
      dispatch(physicalRiskStore.actions.setActive(null))
    }

    hadActiveFilters.current = hasActiveFilters
  }, [dispatch, hasActiveFilters])

  //Une seule famille a afficher : la mosaique n'apporte rien, on ouvre directement son overview.
  //Redirection en "replace" pour que le retour ne reboucle pas sur cette route.
  const familiesForType = filterRiskFamiliesWithRisks(families, risks)
  const singleFamily = !hasActiveFilters && !activeRisk.EstablishmentId && familiesForType.length === 1

  //Content of the page
  function PageContent() {
    if (activeRisk.EstablishmentId) {
      return (
        <div className="flex flex-wrap">
          <PhysicalRiskCard risk={activeRisk} />
        </div>
      )
    }

    if (hasActiveFilters) {
      return (
        <div className="flex flex-wrap">
          {risks.map((risk) => (
            <PhysicalRiskCard risk={risk} key={risk.id} />
          ))}
        </div>
      )
    } else {
      return <RiskFamilies hideEmpty />
    }
  }

  //Meme comportement que le bouton "Ajouter un risque" du home : creation hors contexte.
  const actions = [
    {
      id: "ADD_RISK",
      icon: faPlus,
      label: "Ajouter risque",
      className: "primary" as const,
      isLarge: true,
      onClick: () => setIsAddModalOpen(true)
    }
  ]

  if (singleFamily) {
    return <Navigate to={getPath(`${RISK_FAMILIES_PATH}/${familiesForType[0].id}`)} replace />
  }

  return (
    <NavEstablishment
      actions={actions}
      onTabClick={() => {
        dispatch(riskFamilyStore.actions.setActive(null))
        dispatch(physicalRiskStore.actions.setActive(null))
      }}>
      {isAddModalOpen && (
        <PhysicalRiskEditModal onClose={() => setIsAddModalOpen(false)} context={{ type: "generic" }} />
      )}

      <div className="flex fixed top-[48px] left-0 h-[calc(100%-48px)] app-content-width">
        <div className="py-20 px-40 flex-1 flex flex-col overflow-auto">
          <PageHeader
            section="Risques"
            title="Risques physiques"
            subtitle="Recensez les risques dans votre établissement."
          />
          {hasActiveFilters || activeRisk.EstablishmentId ? (
            PageContent()
          ) : (
            <RiskFamilies
              hideEmpty
              onClickFamily={(family) => navigate(getPath(`${RISK_FAMILIES_PATH}/${family.id}`))}
            />
          )}
        </div>
        <RiskFamiliesFilterPanel showDisplayMode={!hasActiveFilters && !activeRisk.EstablishmentId} />
      </div>
    </NavEstablishment>
  )
}
